import {
  addDays,
  eachDayOfInterval,
  endOfWeek,
  format,
  isWithinInterval,
  parseISO,
  startOfWeek,
} from 'date-fns';
import type { PlanVsActualWeekReportResponse } from '../services/api';
import type {
  ComplianceAlertItem,
  CostBreakdownItem,
  DailyCostPoint,
  EmployeeOption,
  EmployeeSpotlightRow,
  EmployeeStatItem,
  EmployeeStatSection,
  KpiCardData,
  LeaveAbsenceItem,
  ReportAttentionItem,
} from '../pages/OverviewDashboard/types';
import type { DashboardSummary } from '../services/api';
import type { Employee, HolidayRequest, Location, PayRecord, Schedule, Shift, Timecard, TimecardDay } from '../types';
import { computeLateAndEarlyMinutes } from './earlyLateReport';
import {
  buildActivePayRecordMap,
  calculateHourlyRateFromPayRecord,
  getShiftLaborCost,
} from './laborCost';

export const CHART_COLORS = ['#3B82F6', '#8B5CF6', '#14B8A6', '#F59E0B', '#94A3B8', '#EC4899', '#6366F1'];

const AVATAR_COLORS = ['bg-blue-500', 'bg-pink-500', 'bg-violet-500', 'bg-teal-500', 'bg-amber-500', 'bg-indigo-500'];

export function getActualPaidHours(day: TimecardDay): number {
  if (day.totalHours != null) return Number(day.totalHours) || 0;
  return 0;
}

export function getScheduledHours(day: TimecardDay): number {
  if (!day.scheduledIn || !day.scheduledOut) return 0;
  const start = parseISO(day.scheduledIn);
  const end = parseISO(day.scheduledOut);
  let totalMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
  if (totalMinutes <= 0) totalMinutes += 24 * 60;
  const breakPaid = day.paidBreakPaid ?? false;
  if (!breakPaid) totalMinutes -= day.scheduledBreakMinutes ?? 0;
  return Math.round(Math.max(0, totalMinutes / 60) * 100) / 100;
}

function getShiftHours(shift: Shift): number {
  if (shift.paidHours != null && Number(shift.paidHours) >= 0) return Number(shift.paidHours);
  try {
    const start = parseISO(shift.startTime);
    const end = parseISO(shift.endTime);
    let minutes = (end.getTime() - start.getTime()) / (1000 * 60);
    if (minutes <= 0) minutes += 24 * 60;
    const unpaid = (shift.unpaidHours ?? 0) * 60;
    return Math.round(Math.max(0, (minutes - unpaid) / 60) * 100) / 100;
  } catch {
    return 0;
  }
}

function getTwoLetterCode(name: string | null | undefined): string {
  if (!name) return '';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  const word = words[0]?.toUpperCase() ?? '';
  return word.length >= 2 ? word.substring(0, 2) : word.padEnd(2, 'X');
}

function initialsFor(firstName: string, lastName: string): string {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || '?';
}

function formatEuro(value: number): string {
  return `€${Math.round(value).toLocaleString('en-IE')}`;
}

function pctChange(current: number, previous: number): { text: string; color: string } {
  if (previous <= 0 && current <= 0) return { text: 'No change vs last week', color: 'text-gray-500' };
  if (previous <= 0) return { text: '↑ vs last week', color: 'text-emerald-600' };
  const change = ((current - previous) / previous) * 100;
  const sign = change >= 0 ? '↑' : '↓';
  const color = change >= 0 ? 'text-emerald-600' : 'text-red-500';
  return { text: `${sign} ${Math.abs(change).toFixed(1)}% vs last week`, color };
}

function countChange(current: number, previous: number, invertGood = false): { text: string; color: string } {
  const diff = current - previous;
  if (diff === 0) return { text: 'No change vs last week', color: 'text-gray-500' };
  const improved = invertGood ? diff < 0 : diff > 0;
  const sign = diff > 0 ? '↑' : '↓';
  const color = improved ? 'text-emerald-600' : 'text-red-500';
  return { text: `${sign} ${Math.abs(diff)} vs last week`, color };
}

function filterShifts(
  shifts: Shift[],
  weekStart: Date,
  weekEnd: Date,
  locationId?: number | null,
): Shift[] {
  const interval = { start: weekStart, end: weekEnd };
  return shifts.filter((shift) => {
    if (locationId && shift.locationId !== locationId) return false;
    try {
      return isWithinInterval(parseISO(shift.startTime), interval);
    } catch {
      return false;
    }
  });
}

function filterTimecards(
  timecards: Timecard[],
  employees: Employee[],
  locationId?: number | null,
): Timecard[] {
  const employeeMap = new Map(employees.map((e) => [e.id, e]));
  return timecards.filter((tc) => {
    const emp = employeeMap.get(tc.employeeId);
    if (!emp) return false;
    if (locationId) {
      const empLocation =
        emp.locationId ?? emp.profile?.mainLocationId ?? emp.profile?.mainLocation?.id ?? emp.location?.id;
      if (empLocation !== locationId) return false;
    }
    return true;
  });
}

function dayCostForTimecardDay(
  day: TimecardDay,
  rate: number,
): number {
  return rate * getActualPaidHours(day);
}

function computeActualCost(
  timecards: Timecard[],
  payRecords: Record<number, PayRecord>,
  weekStart: Date,
  weekEnd: Date,
): number {
  const interval = { start: weekStart, end: weekEnd };
  return timecards
    .filter((tc) => tc.isApproved === true)
    .reduce((total, tc) => {
      const rate = calculateHourlyRateFromPayRecord(payRecords[tc.employeeId]);
      return (
        total +
        tc.days.reduce((sum, day) => {
          if (!day.date) return sum;
          try {
            if (!isWithinInterval(parseISO(day.date), interval)) return sum;
          } catch {
            return sum;
          }
          return sum + dayCostForTimecardDay(day, rate);
        }, 0)
      );
    }, 0);
}

function computePlanCost(
  shifts: Shift[],
  payRecords: Record<number, PayRecord>,
  weekStart: Date,
  weekEnd: Date,
): number {
  const interval = { start: weekStart, end: weekEnd };
  return shifts.reduce((total, shift) => {
    try {
      if (!isWithinInterval(parseISO(shift.startTime), interval)) return total;
    } catch {
      return total;
    }
    return total + getShiftLaborCost(shift, payRecords);
  }, 0);
}

function computeDailyCosts(
  shifts: Shift[],
  timecards: Timecard[],
  payRecords: Record<number, PayRecord>,
  weekStart: Date,
  weekEnd: Date,
): DailyCostPoint[] {
  const days = eachDayOfInterval({ start: weekStart, end: weekEnd });
  return days.map((day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    const dayShifts = shifts.filter((shift) => {
      try {
        return format(parseISO(shift.startTime), 'yyyy-MM-dd') === dateStr;
      } catch {
        return false;
      }
    });
    const planned = dayShifts.reduce((sum, shift) => sum + getShiftLaborCost(shift, payRecords), 0);
    const actual = timecards
      .filter((tc) => tc.isApproved === true)
      .reduce((total, tc) => {
        const rate = calculateHourlyRateFromPayRecord(payRecords[tc.employeeId]);
        return (
          total +
          tc.days.reduce((sum, dayEntry) => {
            if (!dayEntry.date) return sum;
            if (format(parseISO(dayEntry.date), 'yyyy-MM-dd') !== dateStr) return sum;
            return sum + dayCostForTimecardDay(dayEntry, rate);
          }, 0)
        );
      }, 0);
    return {
      date: dateStr,
      dateLabel: format(day, 'd MMM'),
      planned: Math.round(planned),
      actual: Math.round(actual),
    };
  });
}

function toBreakdown(items: { name: string; value: number }[]): CostBreakdownItem[] {
  const total = items.reduce((s, i) => s + i.value, 0);
  return items
    .filter((i) => i.value > 0)
    .sort((a, b) => b.value - a.value)
    .map((item, idx) => ({
      name: item.name,
      value: Math.round(item.value),
      percent: total > 0 ? Math.round((item.value / total) * 1000) / 10 : 0,
      color: CHART_COLORS[idx % CHART_COLORS.length],
    }));
}

function computeLocationCosts(
  shifts: Shift[],
  timecards: Timecard[],
  payRecords: Record<number, PayRecord>,
  locations: Location[],
  weekStart: Date,
  weekEnd: Date,
  selectedLocationId?: number | null,
): CostBreakdownItem[] {
  if (selectedLocationId) {
    const loc = locations.find((l) => l.id === selectedLocationId);
    const total = computeActualCost(timecards, payRecords, weekStart, weekEnd);
    if (total <= 0) return [];
    return [{ name: loc?.name ?? 'Selected location', value: Math.round(total), percent: 100, color: CHART_COLORS[0] }];
  }

  const byLocation = new Map<number, number>();
  timecards
    .filter((tc) => tc.isApproved === true)
    .forEach((tc) => {
      const locId = tc.locationId ?? shifts.find((s) => s.employeeId === tc.employeeId)?.locationId;
      if (!locId) return;
      const rate = calculateHourlyRateFromPayRecord(payRecords[tc.employeeId]);
      const interval = { start: weekStart, end: weekEnd };
      const cost = tc.days.reduce((sum, day) => {
        if (!day.date) return sum;
        try {
          if (!isWithinInterval(parseISO(day.date), interval)) return sum;
        } catch {
          return sum;
        }
        return sum + dayCostForTimecardDay(day, rate);
      }, 0);
      byLocation.set(locId, (byLocation.get(locId) ?? 0) + cost);
    });

  const items = Array.from(byLocation.entries()).map(([id, value]) => ({
    name: locations.find((l) => l.id === id)?.name ?? `Location ${id}`,
    value,
  }));
  return toBreakdown(items);
}

function computeScheduleCosts(
  shifts: Shift[],
  timecards: Timecard[],
  schedules: Schedule[],
  payRecords: Record<number, PayRecord>,
  weekStart: Date,
  weekEnd: Date,
): CostBreakdownItem[] {
  const scheduleByCode = new Map<string, Schedule>();
  schedules.forEach((s) => scheduleByCode.set(getTwoLetterCode(s.name), s));

  const bySchedule = new Map<string, number>();

  timecards
    .filter((tc) => tc.isApproved === true)
    .forEach((tc) => {
      const rate = calculateHourlyRateFromPayRecord(payRecords[tc.employeeId]);
      const interval = { start: weekStart, end: weekEnd };
      tc.days.forEach((day) => {
        if (!day.date) return;
        try {
          if (!isWithinInterval(parseISO(day.date), interval)) return;
        } catch {
          return;
        }
        const code = day.scheduleCode ?? '';
        const schedule = schedules.find((s) => s.id === day.scheduleId) ?? scheduleByCode.get(code);
        const name = schedule?.name ?? (code ? `Schedule ${code}` : 'Unscheduled');
        const cost = dayCostForTimecardDay(day, rate);
        bySchedule.set(name, (bySchedule.get(name) ?? 0) + cost);
      });
    });

  if (bySchedule.size === 0) {
    shifts.forEach((shift) => {
      try {
        if (!isWithinInterval(parseISO(shift.startTime), { start: weekStart, end: weekEnd })) return;
      } catch {
        return;
      }
      const schedule = schedules.find((s) => s.id === shift.scheduleId);
      const name = schedule?.name ?? 'Other';
      const cost = getShiftLaborCost(shift, payRecords);
      bySchedule.set(name, (bySchedule.get(name) ?? 0) + cost);
    });
  }

  return toBreakdown(Array.from(bySchedule.entries()).map(([name, value]) => ({ name, value })));
}

function computeLateEarlyCounts(timecards: Timecard[], weekStart: Date, weekEnd: Date) {
  const interval = { start: weekStart, end: weekEnd };
  let lateDays = 0;
  let earlyDays = 0;
  let overtimeHours = 0;

  timecards.forEach((tc) => {
    tc.days.forEach((day) => {
      if (!day.date) return;
      try {
        if (!isWithinInterval(parseISO(day.date), interval)) return;
      } catch {
        return;
      }
      const { lateClockInMins, earlyClockOutMins } = computeLateAndEarlyMinutes(day);
      if (lateClockInMins > 0) lateDays += 1;
      if (earlyClockOutMins > 0) earlyDays += 1;
      const scheduled = getScheduledHours(day);
      const actual = getActualPaidHours(day);
      if (actual > scheduled && scheduled > 0) overtimeHours += actual - scheduled;
      overtimeHours += Number(day.extras) || 0;
    });
  });

  return { lateDays, earlyDays, overtimeHours: Math.round(overtimeHours * 10) / 10 };
}

function computeAttendanceRate(timecards: Timecard[], weekStart: Date, weekEnd: Date): number {
  const interval = { start: weekStart, end: weekEnd };
  let scheduled = 0;
  let attended = 0;
  timecards.forEach((tc) => {
    tc.days.forEach((day) => {
      if (!day.date || !day.scheduledIn) return;
      try {
        if (!isWithinInterval(parseISO(day.date), interval)) return;
      } catch {
        return;
      }
      scheduled += 1;
      if (day.actualIn) attended += 1;
    });
  });
  if (scheduled === 0) return 0;
  return Math.round((attended / scheduled) * 1000) / 10;
}

function buildEmployeeSpotlight(
  timecards: Timecard[],
  employees: Employee[],
  schedules: Schedule[],
  payRecords: Record<number, PayRecord>,
  weekStart: Date,
  weekEnd: Date,
): EmployeeSpotlightRow[] {
  const interval = { start: weekStart, end: weekEnd };
  const employeeMap = new Map(employees.map((e) => [e.id, e]));
  const scheduleByCode = new Map(schedules.map((s) => [getTwoLetterCode(s.name), s]));

  const rows: EmployeeSpotlightRow[] = [];

  timecards.forEach((tc) => {
    const emp = employeeMap.get(tc.employeeId);
    if (!emp) return;

    let scheduledDays = 0;
    let attendedDays = 0;
    let late = 0;
    let earlyLeave = 0;
    let overtime = 0;
    let labourCost = 0;
    const rate = calculateHourlyRateFromPayRecord(payRecords[tc.employeeId]);

    tc.days.forEach((day) => {
      if (!day.date) return;
      try {
        if (!isWithinInterval(parseISO(day.date), interval)) return;
      } catch {
        return;
      }
      if (day.scheduledIn) {
        scheduledDays += 1;
        if (day.actualIn) attendedDays += 1;
      }
      const { lateClockInMins, earlyClockOutMins } = computeLateAndEarlyMinutes(day);
      if (lateClockInMins > 0) late += 1;
      if (earlyClockOutMins > 0) earlyLeave += 1;
      const schedH = getScheduledHours(day);
      const actH = getActualPaidHours(day);
      if (actH > schedH && schedH > 0) overtime += actH - schedH;
      overtime += Number(day.extras) || 0;
      labourCost += dayCostForTimecardDay(day, rate);
    });

    if (scheduledDays === 0) return;

    const primaryCode = tc.days.find((d) => d.scheduleCode)?.scheduleCode ?? '';
    const schedule = scheduleByCode.get(primaryCode);
    const role = schedule?.name ?? emp.profile?.mainPosition?.title ?? emp.profile?.positionName ?? 'Employee';

    rows.push({
      id: emp.id,
      name: `${emp.firstName} ${emp.lastName}`.trim(),
      initials: initialsFor(emp.firstName, emp.lastName),
      avatarColor: AVATAR_COLORS[emp.id % AVATAR_COLORS.length],
      profileImageUrl: emp.profileImageUrl,
      updatedAt: emp.updatedAt,
      role,
      attendance: Math.round((attendedDays / scheduledDays) * 1000) / 10,
      late,
      earlyLeave,
      overtime: Math.round(overtime * 10) / 10,
      labourCost: formatEuro(labourCost),
    });
  });

  return rows.sort((a, b) => b.attendance - a.attendance).slice(0, 5);
}

function buildEmployeeStats(
  employeeId: number,
  timecards: Timecard[],
  employees: Employee[],
  schedules: Schedule[],
  payRecords: Record<number, PayRecord>,
  summary: DashboardSummary | null,
  holidayRequests: HolidayRequest[],
  weekStart: Date,
  weekEnd: Date,
): EmployeeStatItem[] {
  const emp = employees.find((e) => e.id === employeeId);
  const tc = timecards.find((t) => t.employeeId === employeeId);
  const interval = { start: weekStart, end: weekEnd };
  const scheduleByCode = new Map(schedules.map((s) => [getTwoLetterCode(s.name), s]));

  let scheduledHours = 0;
  let actualHours = 0;
  let scheduledDays = 0;
  let attendedDays = 0;
  let overtime = 0;
  let labourCost = 0;
  const rate = calculateHourlyRateFromPayRecord(payRecords[employeeId]);

  tc?.days.forEach((day) => {
    if (!day.date) return;
    try {
      if (!isWithinInterval(parseISO(day.date), interval)) return;
    } catch {
      return;
    }
    scheduledHours += getScheduledHours(day);
    actualHours += getActualPaidHours(day);
    if (day.scheduledIn) {
      scheduledDays += 1;
      if (day.actualIn) attendedDays += 1;
    }
    const schedH = getScheduledHours(day);
    const actH = getActualPaidHours(day);
    if (actH > schedH && schedH > 0) overtime += actH - schedH;
    overtime += Number(day.extras) || 0;
    labourCost += dayCostForTimecardDay(day, rate);
  });

  const attendance = scheduledDays > 0 ? Math.round((attendedDays / scheduledDays) * 1000) / 10 : 0;

  const weekHolidayDays = holidayRequests
    .filter((h) => h.employeeId === employeeId && h.status?.toLowerCase() === 'approved')
    .flatMap((h) => h.holidayTypes ?? [])
    .filter((d) => {
      try {
        const dt = parseISO(d.date);
        return isWithinInterval(dt, interval);
      } catch {
        return false;
      }
    });

  const sickDays = weekHolidayDays.filter((d) =>
    (d.holidayType ?? '').toLowerCase().includes('sick'),
  ).length;
  const annualDays = weekHolidayDays.filter((d) =>
    (d.holidayType ?? '').toLowerCase().includes('annual'),
  ).length;

  const visaCount = summary?.visaAlerts.filter((v) => v.employeeId === employeeId).length ?? 0;
  const licenseCount =
    (summary?.licenseExpiringSoon.filter((l) => l.employeeId === employeeId).length ?? 0) +
    (summary?.licenseExpired.filter((l) => l.employeeId === employeeId).length ?? 0);
  const trainingCount = summary?.upcomingTraining.filter((t) => t.employeeId === employeeId).length ?? 0;
  const docsExpiring = visaCount + licenseCount + trainingCount;

  const primaryCode = tc?.days.find((d) => d.scheduleCode)?.scheduleCode ?? '';
  const schedule = scheduleByCode.get(primaryCode);

  return [
    { label: 'Scheduled Hours', value: Math.round(scheduledHours * 10) / 10 },
    { label: 'Actual Hours', value: Math.round(actualHours * 10) / 10 },
    { label: 'Attendance %', value: `${attendance}%` },
    { label: 'Sick Days', value: sickDays },
    { label: 'Annual Leave', value: annualDays > 0 ? `${annualDays} day${annualDays === 1 ? '' : 's'}` : '0 days' },
    { label: 'Overtime', value: `${Math.round(overtime * 10) / 10} hrs` },
    { label: 'Total Labour Cost', value: formatEuro(labourCost) },
    { label: 'Documents Expiring', value: docsExpiring },
    { label: 'Incidents / Notes', value: '—' },
  ];
}

export function groupEmployeeStatSections(stats: EmployeeStatItem[]): EmployeeStatSection[] {
  const byLabel = new Map(stats.map((s) => [s.label, s]));
  const pick = (labels: string[]) =>
    labels.map((label) => byLabel.get(label)).filter((s): s is EmployeeStatItem => !!s);

  return [
    {
      id: 'hours-attendance',
      title: 'Hours & Attendance',
      stats: pick(['Scheduled Hours', 'Actual Hours', 'Attendance %', 'Overtime']),
    },
    {
      id: 'leave',
      title: 'Leave',
      stats: pick(['Sick Days', 'Annual Leave']),
    },
    {
      id: 'labour-cost',
      title: 'Labour Cost',
      stats: pick(['Total Labour Cost']),
    },
    {
      id: 'compliance',
      title: 'Compliance',
      stats: pick(['Documents Expiring', 'Incidents / Notes']),
    },
  ].filter((section) => section.stats.length > 0);
}

export function buildEmployeeStatSections(
  employeeId: number,
  timecards: Timecard[],
  employees: Employee[],
  schedules: Schedule[],
  payRecords: Record<number, PayRecord>,
  summary: DashboardSummary | null,
  holidayRequests: HolidayRequest[],
  weekStart: Date,
  weekEnd: Date,
): EmployeeStatSection[] {
  return groupEmployeeStatSections(
    buildEmployeeStats(
      employeeId,
      timecards,
      employees,
      schedules,
      payRecords,
      summary,
      holidayRequests,
      weekStart,
      weekEnd,
    ),
  );
}

function countLeaveInWeek(
  holidayRequests: HolidayRequest[],
  weekStart: Date,
  weekEnd: Date,
  locationEmployeeIds: Set<number> | null,
): LeaveAbsenceItem[] {
  const interval = { start: weekStart, end: weekEnd };
  let annual = 0;
  let sick = 0;
  let other = 0;

  holidayRequests
    .filter((h) => h.status?.toLowerCase() === 'approved')
    .forEach((h) => {
      if (locationEmployeeIds && !locationEmployeeIds.has(h.employeeId)) return;
      (h.holidayTypes ?? []).forEach((d) => {
        try {
          if (!isWithinInterval(parseISO(d.date), interval)) return;
        } catch {
          return;
        }
        const type = (d.holidayType ?? h.leaveType?.name ?? '').toLowerCase();
        if (type.includes('sick')) sick += 1;
        else if (type.includes('annual')) annual += 1;
        else other += 1;
      });
    });

  return [
    { label: 'Annual Leave', count: annual, icon: 'calendar', color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Sick Leave', count: sick, icon: 'heart', color: 'text-red-500', bg: 'bg-red-50' },
    { label: 'Other Absences', count: other, icon: 'user-x', color: 'text-orange-500', bg: 'bg-orange-50' },
    { label: 'No-Shows', count: 0, icon: 'x-circle', color: 'text-gray-500', bg: 'bg-gray-100' },
  ];
}

function employeeName(firstName: string, lastName: string): string {
  return `${firstName} ${lastName}`.trim();
}

function formatShortDate(value: string | undefined | null): string {
  if (!value) return '';
  try {
    return format(parseISO(value), 'd MMM yyyy');
  } catch {
    return value;
  }
}

function attendanceDeepLink(dateStr: string, recordId?: number): string {
  let weekStr = dateStr;
  try {
    weekStr = format(startOfWeek(parseISO(dateStr), { weekStartsOn: 1 }), 'yyyy-MM-dd');
  } catch {
    /* keep dateStr */
  }
  const params = new URLSearchParams({ week: weekStr, date: dateStr });
  if (recordId != null) params.set('recordId', String(recordId));
  return `/attendance?${params.toString()}`;
}

function todayAttendanceHref(): string {
  const today = format(new Date(), 'yyyy-MM-dd');
  return attendanceDeepLink(today);
}

export function buildReportsNeedingAttention(summary: DashboardSummary | null): ReportAttentionItem[] {
  if (!summary) return [];
  const items: ReportAttentionItem[] = [];

  if (summary.missedClockout.length > 0) {
    items.push({
      id: 'missed-clockout',
      title: `${summary.missedClockout.length} Missing Clock-out${summary.missedClockout.length === 1 ? '' : 's'}`,
      description: 'Employees who forgot to clock out',
      icon: 'clock',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-500',
      href: '/attendance',
      hrefLabel: 'Open Attendance',
      details: summary.missedClockout.map((row) => ({
        id: `missed-${row.recordId}`,
        primary: employeeName(row.firstName, row.lastName),
        secondary: `Clocked in ${formatShortDate(row.date)} · ${row.hoursClocked} hrs`,
        href: attendanceDeepLink(row.date, row.recordId),
      })),
    });
  }

  if (summary.absentToday.length > 0) {
    items.push({
      id: 'absent-today',
      title: `${summary.absentToday.length} Absent Today`,
      description: 'Scheduled employees with no clock-in today',
      icon: 'alert',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-500',
      href: todayAttendanceHref(),
      hrefLabel: 'Open Attendance',
      details: summary.absentToday.map((row) => ({
        id: `absent-${row.employeeId}-${row.date}`,
        primary: employeeName(row.firstName, row.lastName),
        secondary: `Scheduled ${formatShortDate(row.date)} · no clock-in`,
        href: `/employees/${row.employeeId}?tab=notes`,
      })),
    });
  }

  const lateToday = summary.lateClockInToday ?? [];
  if (lateToday.length > 0) {
    items.push({
      id: 'late-clock-in-today',
      title: `${lateToday.length} Late Clock-in${lateToday.length === 1 ? '' : 's'} Today`,
      description: 'Employees who clocked in after their scheduled start',
      icon: 'clock',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      href: '/reports/timecards-early-late',
      hrefLabel: 'Open Early/Late Report',
      details: lateToday.map((row, idx) => ({
        id: `late-${row.employeeId}-${idx}`,
        primary: employeeName(row.firstName, row.lastName),
        secondary: `${row.minutesLate} min late · ${row.schedule || 'Shift'}`,
        href: todayAttendanceHref(),
      })),
    });
  }

  const earlyToday = summary.earlyClockOutToday ?? [];
  if (earlyToday.length > 0) {
    items.push({
      id: 'early-clock-out-today',
      title: `${earlyToday.length} Early Clock-out${earlyToday.length === 1 ? '' : 's'} Today`,
      description: 'Employees who left before their scheduled end',
      icon: 'clock',
      iconBg: 'bg-yellow-100',
      iconColor: 'text-yellow-700',
      href: '/reports/timecards-early-late',
      hrefLabel: 'Open Early/Late Report',
      details: earlyToday.map((row, idx) => ({
        id: `early-${row.employeeId}-${idx}`,
        primary: employeeName(row.firstName, row.lastName),
        secondary: `${row.minutesEarly} min early · ${row.schedule || 'Shift'}`,
        href: todayAttendanceHref(),
      })),
    });
  }

  const pendingHolidays = summary.pendingHolidayRequests ?? [];
  if (summary.counts.pendingHolidayRequests > 0) {
    items.push({
      id: 'pending-holiday',
      title: `${summary.counts.pendingHolidayRequests} Pending Holiday Requests`,
      description: 'Awaiting manager approval',
      icon: 'calendar',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      href: '/holiday-admin?view=table',
      hrefLabel: 'Open Holiday Admin',
      details: pendingHolidays.map((row, idx) => ({
        id: `holiday-${row.requestId ?? `${row.employeeId}-${row.startDate}-${idx}`}`,
        primary: employeeName(row.firstName, row.lastName),
        secondary: `${row.leaveType} · ${formatShortDate(row.startDate)} – ${formatShortDate(row.endDate)}`,
        href: row.requestId
          ? `/holiday-admin?requestId=${row.requestId}&view=table`
          : '/holiday-admin?view=table',
      })),
    });
  }

  if (summary.licenseExpired.length > 0) {
    items.push({
      id: 'license-expired',
      title: `${summary.licenseExpired.length} Expired License${summary.licenseExpired.length === 1 ? '' : 's'}`,
      description: 'Staff licenses past expiry date',
      icon: 'file',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      href: '/employees',
      details: summary.licenseExpired.map((row) => ({
        id: `license-exp-${row.licenseId}`,
        primary: employeeName(row.firstName, row.lastName),
        secondary: `${row.licenseType || 'License'} ${row.licenseNumber} · expired ${formatShortDate(row.expiryDate)} (${row.daysSinceExpiry}d)`,
        href: `/employees/${row.employeeId}?tab=licenses`,
      })),
    });
  }

  if (summary.licenseExpiringSoon.length > 0) {
    items.push({
      id: 'license-expiring',
      title: `${summary.licenseExpiringSoon.length} License${summary.licenseExpiringSoon.length === 1 ? '' : 's'} Expiring Soon`,
      description: 'Renewals due within 30 days',
      icon: 'file',
      iconBg: 'bg-violet-100',
      iconColor: 'text-violet-600',
      href: '/employees',
      details: summary.licenseExpiringSoon.map((row) => ({
        id: `license-soon-${row.licenseId}`,
        primary: employeeName(row.firstName, row.lastName),
        secondary: `${row.licenseType || 'License'} ${row.licenseNumber} · expires ${formatShortDate(row.expiryDate)} (${row.daysUntilExpiry}d)`,
        href: `/employees/${row.employeeId}?tab=licenses`,
      })),
    });
  }

  if (summary.visaAlerts.length > 0) {
    items.push({
      id: 'visa-alerts',
      title: `${summary.visaAlerts.length} Visa Alert${summary.visaAlerts.length === 1 ? '' : 's'}`,
      description: 'Work visas expiring within 30 days',
      icon: 'file',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      href: '/employees',
      details: summary.visaAlerts.map((row) => ({
        id: `visa-${row.employeeId}-${row.visaNumber}`,
        primary: employeeName(row.firstName, row.lastName),
        secondary: `Visa ${row.visaNumber || '—'} · ${row.daysUntilExpiry < 0 ? 'expired' : 'expires'} ${formatShortDate(row.visaExpiryDate)}`,
        href: `/employees/${row.employeeId}?tab=personal&scrollToVisa=true#visa-details`,
      })),
    });
  }

  if (summary.upcomingTraining.length > 0) {
    items.push({
      id: 'training-due',
      title: `${summary.upcomingTraining.length} Training Item${summary.upcomingTraining.length === 1 ? '' : 's'} Due`,
      description: 'Overdue or upcoming training deadlines',
      icon: 'file',
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600',
      href: '/employees',
      details: summary.upcomingTraining.map((row) => ({
        id: `training-${row.recordId}-${row.dueDate}`,
        primary: employeeName(row.firstName, row.lastName),
        secondary: `${row.label} · ${row.isOverdue ? 'overdue' : 'due'} ${formatShortDate(row.dueDate)}`,
        href: `/employees/${row.employeeId}?tab=training`,
      })),
    });
  }

  if (items.length === 0) {
    items.push({
      id: 'all-clear',
      title: 'All Clear',
      description: 'No urgent reports need attention right now',
      icon: 'file',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      href: '/reports',
      hrefLabel: 'Open Reports',
    });
  }

  return items.slice(0, 5);
}

export interface WeekMetricsInput {
  report: PlanVsActualWeekReportResponse;
  weekStart: Date;
  weekEnd: Date;
  locationId?: number | null;
  locations: Location[];
  employees: Employee[];
}

export function computeWeekMetrics(input: WeekMetricsInput) {
  const payRecords = buildActivePayRecordMap(input.report.payRecords);
  const employees = input.employees.length ? input.employees : input.report.employees.map((e) => ({
    id: e.id,
    firstName: e.firstName,
    lastName: e.lastName,
    isActive: e.isActive,
    locationId: e.mainLocationId,
    profile: { mainLocationId: e.mainLocationId },
  } as Employee));

  const shifts = filterShifts(input.report.shifts, input.weekStart, input.weekEnd, input.locationId);
  const timecards = filterTimecards(input.report.timecards, employees, input.locationId);

  const planCost = computePlanCost(shifts, payRecords, input.weekStart, input.weekEnd);
  const actualCost = computeActualCost(timecards, payRecords, input.weekStart, input.weekEnd);
  const planVsActualPct = planCost > 0 ? Math.round((actualCost / planCost) * 1000) / 10 : 0;
  const { lateDays, earlyDays, overtimeHours } = computeLateEarlyCounts(timecards, input.weekStart, input.weekEnd);
  const attendanceRate = computeAttendanceRate(timecards, input.weekStart, input.weekEnd);

  return {
    payRecords,
    shifts,
    timecards,
    employees,
    planCost,
    actualCost,
    planVsActualPct,
    lateDays,
    earlyDays,
    overtimeHours,
    attendanceRate,
    dailyCosts: computeDailyCosts(shifts, timecards, payRecords, input.weekStart, input.weekEnd),
    locationCosts: computeLocationCosts(
      shifts,
      timecards,
      payRecords,
      input.locations,
      input.weekStart,
      input.weekEnd,
      input.locationId,
    ),
    departmentCosts: computeScheduleCosts(
      shifts,
      timecards,
      input.report.schedules,
      payRecords,
      input.weekStart,
      input.weekEnd,
    ),
    employeeSpotlight: buildEmployeeSpotlight(
      timecards,
      employees,
      input.report.schedules,
      payRecords,
      input.weekStart,
      input.weekEnd,
    ),
  };
}

export function buildKpiCards(
  current: ReturnType<typeof computeWeekMetrics>,
  previous: ReturnType<typeof computeWeekMetrics> | null,
): KpiCardData[] {
  const costTrend = pctChange(current.actualCost, previous?.actualCost ?? 0);
  const underOver = current.planCost - current.actualCost;
  const planSubtext =
    underOver >= 0
      ? `${formatEuro(underOver)} under plan`
      : `${formatEuro(Math.abs(underOver))} over plan`;
  const planColor = underOver >= 0 ? 'text-emerald-600' : 'text-red-500';

  const attendanceTrend = previous
    ? pctChange(current.attendanceRate, previous.attendanceRate)
    : { text: 'This week', color: 'text-gray-500' };

  const lateTrend = countChange(current.lateDays, previous?.lateDays ?? current.lateDays, true);
  const earlyTrend = countChange(current.earlyDays, previous?.earlyDays ?? current.earlyDays, true);
  const otTrend = pctChange(current.overtimeHours, previous?.overtimeHours ?? 0);

  return [
    {
      title: 'Total Labour Cost',
      value: formatEuro(current.actualCost),
      trend: costTrend.text,
      trendColor: costTrend.color,
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      icon: 'euro',
    },
    {
      title: 'Planned vs Actual Labour',
      value: `${current.planVsActualPct}%`,
      trend: planSubtext,
      trendColor: planColor,
      iconBg: 'bg-violet-100',
      iconColor: 'text-violet-600',
      icon: 'bar',
    },
    {
      title: 'Attendance Rate',
      value: `${current.attendanceRate}%`,
      trend: attendanceTrend.text,
      trendColor: attendanceTrend.color,
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600',
      icon: 'users',
    },
    {
      title: 'Late Clock-ins',
      value: String(current.lateDays),
      trend: lateTrend.text,
      trendColor: lateTrend.color,
      iconBg: 'bg-red-100',
      iconColor: 'text-red-500',
      icon: 'late',
    },
    {
      title: 'Early Clock-outs',
      value: String(current.earlyDays),
      trend: earlyTrend.text,
      trendColor: earlyTrend.color,
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-500',
      icon: 'clock',
    },
    {
      title: 'Overtime Hours',
      value: String(current.overtimeHours),
      trend: otTrend.text,
      trendColor: otTrend.color,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      icon: 'overtime',
    },
  ];
}

export function buildPayrollReadiness(timecards: Timecard[]) {
  const approved = timecards.filter((tc) => tc.isApproved === true).length;
  const rejected = timecards.filter((tc) => tc.isApproved === false).length;
  const pending = timecards.filter((tc) => tc.isApproved == null || tc.isApproved === undefined).length;
  const total = approved + pending + rejected;
  const percent = total > 0 ? Math.round((approved / total) * 100) : 0;
  return { percent, approved, pending, rejected, total };
}

function employeeCountLabel(count: number): string {
  return `${count} employee${count === 1 ? '' : 's'}`;
}

export function buildComplianceAlerts(summary: DashboardSummary | null): ComplianceAlertItem[] {
  if (!summary) {
    return [
      {
        id: 'visas',
        label: 'Visas Expiring',
        count: 0,
        countLabel: '0 employees',
        description: 'Work visas expiring within 30 days',
        icon: 'passport',
        iconBg: 'bg-blue-100',
        iconColor: 'text-blue-600',
        details: [],
      },
      {
        id: 'training',
        label: 'Training Expiring',
        count: 0,
        countLabel: '0 employees',
        description: 'Overdue or upcoming training deadlines',
        icon: 'graduation',
        iconBg: 'bg-violet-100',
        iconColor: 'text-violet-600',
        details: [],
      },
      {
        id: 'licenses',
        label: 'Licenses Expiring',
        count: 0,
        countLabel: '0 employees',
        description: 'Expired licenses or renewals due within 30 days',
        icon: 'badge',
        iconBg: 'bg-teal-100',
        iconColor: 'text-teal-600',
        details: [],
      },
      {
        id: 'absent-today',
        label: 'Absent Today',
        count: 0,
        countLabel: '0 employees',
        description: 'Scheduled employees with no clock-in today',
        icon: 'file-x',
        iconBg: 'bg-red-100',
        iconColor: 'text-red-500',
        details: [],
      },
    ];
  }

  const visaDetails = summary.visaAlerts.map((row) => ({
    id: `visa-${row.employeeId}-${row.visaNumber}`,
    primary: employeeName(row.firstName, row.lastName),
    secondary: `Visa ${row.visaNumber || '—'} · ${row.daysUntilExpiry < 0 ? 'expired' : 'expires'} ${formatShortDate(row.visaExpiryDate)}`,
    href: `/employees/${row.employeeId}?tab=personal&scrollToVisa=true#visa-details`,
  }));

  const trainingDetails = summary.upcomingTraining.map((row) => ({
    id: `training-${row.recordId}-${row.dueDate}`,
    primary: employeeName(row.firstName, row.lastName),
    secondary: `${row.label} · ${row.isOverdue ? 'overdue' : 'due'} ${formatShortDate(row.dueDate)}`,
    href: `/employees/${row.employeeId}?tab=training`,
  }));

  const licenseDetails = [
    ...summary.licenseExpired.map((row) => ({
      id: `license-exp-${row.licenseId}`,
      primary: employeeName(row.firstName, row.lastName),
      secondary: `${row.licenseType || 'License'} ${row.licenseNumber} · expired ${formatShortDate(row.expiryDate)} (${row.daysSinceExpiry}d)`,
      href: `/employees/${row.employeeId}?tab=licenses`,
    })),
    ...summary.licenseExpiringSoon.map((row) => ({
      id: `license-soon-${row.licenseId}`,
      primary: employeeName(row.firstName, row.lastName),
      secondary: `${row.licenseType || 'License'} ${row.licenseNumber} · expires ${formatShortDate(row.expiryDate)} (${row.daysUntilExpiry}d)`,
      href: `/employees/${row.employeeId}?tab=licenses`,
    })),
  ];

  const absentDetails = summary.absentToday.map((row) => ({
    id: `absent-${row.employeeId}-${row.date}`,
    primary: employeeName(row.firstName, row.lastName),
    secondary: `Scheduled ${formatShortDate(row.date)} · no clock-in`,
    href: `/employees/${row.employeeId}?tab=notes`,
  }));

  return [
    {
      id: 'visas',
      label: 'Visas Expiring',
      count: visaDetails.length,
      countLabel: employeeCountLabel(visaDetails.length),
      description: 'Work visas expiring within 30 days',
      icon: 'passport',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      details: visaDetails,
    },
    {
      id: 'training',
      label: 'Training Expiring',
      count: trainingDetails.length,
      countLabel: employeeCountLabel(trainingDetails.length),
      description: 'Overdue or upcoming training deadlines',
      icon: 'graduation',
      iconBg: 'bg-violet-100',
      iconColor: 'text-violet-600',
      details: trainingDetails,
    },
    {
      id: 'licenses',
      label: 'Licenses Expiring',
      count: licenseDetails.length,
      countLabel: employeeCountLabel(licenseDetails.length),
      description: 'Expired licenses or renewals due within 30 days',
      icon: 'badge',
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600',
      details: licenseDetails,
    },
    {
      id: 'absent-today',
      label: 'Absent Today',
      count: absentDetails.length,
      countLabel: employeeCountLabel(absentDetails.length),
      description: 'Scheduled employees with no clock-in today',
      icon: 'file-x',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-500',
      details: absentDetails,
    },
  ];
}

export function buildEmployeeOptions(
  employees: Employee[],
  schedules: Schedule[],
  timecards: Timecard[],
): EmployeeOption[] {
  const scheduleByCode = new Map(schedules.map((s) => [getTwoLetterCode(s.name), s]));
  const withTimecards = new Set(timecards.map((t) => t.employeeId));

  return employees
    .filter((e) => withTimecards.has(e.id))
    .map((emp) => {
      const tc = timecards.find((t) => t.employeeId === emp.id);
      const code = tc?.days.find((d) => d.scheduleCode)?.scheduleCode ?? '';
      const schedule = scheduleByCode.get(code);
      return {
        id: emp.id,
        name: `${emp.firstName} ${emp.lastName}`.trim(),
        role: schedule?.name ?? emp.profile?.mainPosition?.title ?? emp.profile?.positionName ?? 'Employee',
        initials: initialsFor(emp.firstName, emp.lastName),
        avatarColor: AVATAR_COLORS[emp.id % AVATAR_COLORS.length],
        profileImageUrl: emp.profileImageUrl,
        updatedAt: emp.updatedAt,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function formatWeekRangeLabel(weekStart: Date, weekEnd: Date): string {
  return `${format(weekStart, 'd MMM')} – ${format(weekEnd, 'd MMM yyyy')}`;
}

export function getWeekBounds(currentWeek: Date, weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6) {
  const weekStart = startOfWeek(currentWeek, { weekStartsOn });
  const weekEnd = endOfWeek(currentWeek, { weekStartsOn });
  const apiWeekStart = startOfWeek(weekStart, { weekStartsOn: 1 });
  const prevWeekStart = addDays(weekStart, -7);
  const prevWeekEnd = addDays(weekEnd, -7);
  const prevApiWeekStart = startOfWeek(prevWeekStart, { weekStartsOn: 1 });
  return { weekStart, weekEnd, apiWeekStart, prevWeekStart, prevWeekEnd, prevApiWeekStart };
}

export function buildEmployeeStatsForId(
  employeeId: number,
  current: ReturnType<typeof computeWeekMetrics>,
  summary: DashboardSummary | null,
  holidayRequests: HolidayRequest[],
  weekStart: Date,
  weekEnd: Date,
  schedules: Schedule[],
): EmployeeStatItem[] {
  return buildEmployeeStats(
    employeeId,
    current.timecards,
    current.employees,
    schedules,
    current.payRecords,
    summary,
    holidayRequests,
    weekStart,
    weekEnd,
  );
}

export function buildEmployeeStatSectionsForId(
  employeeId: number,
  current: ReturnType<typeof computeWeekMetrics>,
  summary: DashboardSummary | null,
  holidayRequests: HolidayRequest[],
  weekStart: Date,
  weekEnd: Date,
  schedules: Schedule[],
): EmployeeStatSection[] {
  return buildEmployeeStatSections(
    employeeId,
    current.timecards,
    current.employees,
    schedules,
    current.payRecords,
    summary,
    holidayRequests,
    weekStart,
    weekEnd,
  );
}

export function eachWeekStartInRange(
  fromWeek: Date,
  toWeek: Date,
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6,
): Date[] {
  const start = startOfWeek(fromWeek, { weekStartsOn });
  const end = startOfWeek(toWeek, { weekStartsOn });
  const rangeStart = start <= end ? start : end;
  const rangeEnd = start <= end ? end : start;
  const weeks: Date[] = [];
  let cursor = rangeStart;
  while (cursor <= rangeEnd) {
    weeks.push(cursor);
    cursor = addDays(cursor, 7);
  }
  return weeks;
}

export { countLeaveInWeek };
